import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '../../store/editorStore';

interface HiddenWallsInfoPanelProps {
  isVisible: boolean;
}

/**
 * Banner shown in 3D when any walls are temporarily hidden.
 * Recovery path when a hidden wall can no longer be clicked.
 */
const HiddenWallsInfoPanel: React.FC<HiddenWallsInfoPanelProps> = observer(({ isVisible }) => {
  const { t } = useTranslation();
  const editorStore = useEditorStore();
  const hiddenCount = editorStore.hiddenWallIds.size;

  if (!editorStore.view3D || hiddenCount === 0) {
    return null;
  }

  return (
    <div
      className={`absolute ${isVisible ? 'top-[88px]' : 'top-[71px]'} left-1/2 z-[9998] flex max-w-xl -translate-x-1/2 items-center gap-3 rounded-md border border-gray-300 bg-toolbkg px-3 py-1.5 shadow-sm`}
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
        <span>{t('cameraViews.hiddenWallsBanner', { count: hiddenCount })}</span>
        <span className="text-gray-400">·</span>
        <span className="text-gray-500">
          {t('cameraViews.hiddenWallsShortcutHint')}
          {' '}
          <kbd className="rounded border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[10px] leading-tight">
            H
          </kbd>
        </span>
      </div>
      <button
        type="button"
        onClick={() => editorStore.clearHiddenWalls()}
        className="ml-auto flex-shrink-0 rounded bg-[#66645F] px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-[#55534e]"
        title={t('cameraViews.showAllWallsTooltip')}
        aria-label={t('cameraViews.showAllWallsAriaLabel')}
      >
        {t('cameraViews.showAllWalls')}
      </button>
    </div>
  );
});

export default HiddenWallsInfoPanel;
